<template>
  <component
    :is="tag"
    class="button"
    :class="[
      colorClass,
      heightClass,
      fontSizeClass,
      {
        'button--loading': isSubmitting,
        'button--text': isText,
        'button--inactive': isInactive,
      },
    ]"
    :disabled="isDeactivated"
    v-on="$listeners"
  >
    <span :class="['button__inner', { 'button__inner--transparent': isSubmitting }]">
      <slot></slot>
    </span>
  </component>
</template>

<script>
import { shakeElement } from '@/helpers/animation'

export const BUTTON_STYLES = {
  color: {
    black: 'black',
    white: 'white',
    storm: 'storm',
  },
  fontSize: {
    normal: 'normal',
    small: 'small',
  },
  height: {
    normal: 'normal',
    large: 'large',
  },
}

export default {
  name: 'Button',

  props: {
    tag: {
      type: String,
      default: 'button',
    },

    color: {
      type: String,
      default: BUTTON_STYLES.color.black,
      validator(value) {
        return Object.values(BUTTON_STYLES.color).includes(value)
      },
    },

    fontSize: {
      type: String,
      default: BUTTON_STYLES.fontSize.normal,
      validator(value) {
        return Object.values(BUTTON_STYLES.fontSize).includes(value)
      },
    },

    height: {
      type: String,
      default: BUTTON_STYLES.height.normal,
      validator(value) {
        return Object.values(BUTTON_STYLES.height).includes(value)
      },
    },

    isText: {
      type: Boolean,
      default: false,
    },

    isDisabled: {
      type: Boolean,
      default: false,
    },

    // Same look and feel as "isDisabled" but click events are not blocked
    isInactive: {
      type: Boolean,
      default: false,
    },

    isSubmitting: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    isDeactivated() {
      return this.isDisabled || this.isSubmitting
    },

    colorClass() {
      return this.color !== BUTTON_STYLES.color.black ? `button--color-${this.color}` : ''
    },

    fontSizeClass() {
      return this.fontSize !== BUTTON_STYLES.fontSize.normal
        ? `button--font-size-${this.fontSize}`
        : ''
    },

    heightClass() {
      return this.height !== BUTTON_STYLES.height.normal ? `button--height-${this.height}` : ''
    },
  },

  methods: {
    shake() {
      shakeElement(this.$el)
    },
  },
}
</script>

<style scoped lang="scss">
@import './button-settings.scss';

.button {
  @include button;
  @include transition((background-color, color), $transition-time-fast);

  &--loading {
    &:before {
      @include spinner-centered($spinner-size, $color-white, 1px);
    }
  }

  &--font-size-small {
    font-size: $font-size-small;
  }

  &--height-large {
    padding: 27px 26px 26px;
  }

  &--color-storm {
    background-color: $color-storm-blue;

    &:focus,
    &:hover {
      background-color: $color-grey-dark-2;
    }
  }

  &--color-white {
    background-color: $color-white;
    border: 1px solid $color-text-lighter;
    color: $color-text;

    &:focus,
    &:hover {
      background-color: $color-grey-light-4;
      border: 1px solid $color-text-light;
      color: $color-black;
    }

    &.button--inactive,
    &:disabled {
      background-color: $color-white;
      border: 1px solid $color-text-lighter;
      color: $color-text-lighter;

      &:focus,
      &:hover {
        background-color: $color-white;
      }
    }

    &.button--loading {
      &:before {
        @include spinner-centered($spinner-size, $color-text, 1px);
      }
    }
  }

  &--text {
    background-color: transparent;
    color: $color-text;

    &:focus,
    &:hover {
      background-color: transparent;
      text-decoration: underline;
    }

    &:disabled {
      background-color: transparent;
      color: $color-text-lighter;
    }

    &.button--loading {
      &:before {
        @include spinner-centered($spinner-size, $color-text, 1px);
      }
    }
  }

  &__inner {
    &--transparent {
      opacity: 0;
    }
  }
}
</style>
